<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import OgKeysLogo from './OgKeysLogo.vue';
import PaymentMarks from './PaymentMarks.vue';
import BrandMark from './BrandMark.vue';
import { socialIcon } from '../utils/brand-icons';
import { isHttpUrl } from '../utils/safe-url';
import { useCompanyInfo } from '../composables/useCompanyInfo';
import { fetchSocialLinks, type SocialLink } from '../api/cms';

const year = new Date().getFullYear();
const { company } = useCompanyInfo();
const socialLinks = ref<SocialLink[]>([]);

const companyName = computed(() => company.value?.name || 'GladKey');
const hasAddress = computed(() =>
  company.value && (company.value.addressLine1 || company.value.city || company.value.country),
);
const addressLine = computed(() => {
  if (!company.value) return '';
  const parts = [
    company.value.addressLine1,
    company.value.city,
    company.value.postalCode,
    company.value.country,
  ].filter(Boolean);
  return parts.join(', ');
});

onMounted(async () => {
  try {
    // Never render a non-http(s) href (e.g. a legacy `javascript:` row).
    socialLinks.value = (await fetchSocialLinks()).filter((l) => isHttpUrl(l.url));
  } catch {
    // social links unavailable
  }
});
</script>

<template>
  <footer class="bg-[#1d1b1a] text-white rounded-t-[32px]">
    <div class="max-w-[1280px] mx-auto px-6 md:px-20 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div class="lg:col-span-6 flex flex-col justify-between gap-12">
        <div>
          <OgKeysLogo class="h-12 w-auto invert" />
          <p class="mt-5 max-w-[384px] text-[16px] leading-[1.55] text-[#868381]">
            {{ $t('footer.tagline') }}
          </p>
          <address v-if="company" class="not-italic font-mono text-[13px] leading-[1.6] text-[#868381] mt-4">
            <p>{{ companyName }}<template v-if="company.registrationNumber">, reg. {{ company.registrationNumber }}</template></p>
            <p v-if="hasAddress">{{ addressLine }}</p>
            <p v-if="company.email">
              <a :href="'mailto:' + company.email" class="hover:opacity-80">{{ company.email }}</a>
            </p>
          </address>
          <!-- Social Links -->
          <div v-if="socialLinks.length" class="flex items-center gap-4 mt-5">
            <a
              v-for="link in socialLinks"
              :key="link.id"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-[#868381] hover:text-white transition-colors min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
              :title="link.platform"
            >
              <BrandMark v-if="socialIcon(link.platform)" :icon="socialIcon(link.platform)!" class="w-5 h-5" />
              <span v-else class="text-xs font-mono uppercase">{{ link.platform }}</span>
            </a>
          </div>
        </div>
        <div class="flex flex-col gap-4">
          <PaymentMarks />
          <p class="font-mono text-[14px] text-[#868381]">{{ $t('footer.copyright', { year }) }}</p>
        </div>
      </div>
      <div class="lg:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-8">
        <div>
          <p class="font-mono text-[14px] tracking-[0.1em] uppercase text-[#868381] pb-3">{{ $t('footer.shop') }}</p>
          <ul class="space-y-3">
            <li><RouterLink to="/catalog" class="hover:opacity-80">{{ $t('footer.catalog') }}</RouterLink></li>
            <li><RouterLink to="/catalog?sort=price_asc" class="hover:opacity-80">{{ $t('footer.deals') }}</RouterLink></li>
          </ul>
        </div>
        <div>
          <p class="font-mono text-[14px] tracking-[0.1em] uppercase text-[#868381] pb-3">{{ $t('footer.legal') }}</p>
          <ul class="space-y-3">
            <li><RouterLink to="/legal/terms" class="hover:opacity-80">{{ $t('footer.terms') }}</RouterLink></li>
            <li><RouterLink to="/legal/privacy" class="hover:opacity-80">{{ $t('footer.privacy') }}</RouterLink></li>
            <li><RouterLink to="/legal/cookies" class="hover:opacity-80">{{ $t('footer.cookies') }}</RouterLink></li>
          </ul>
        </div>
        <div>
          <p class="font-mono text-[14px] tracking-[0.1em] uppercase text-[#868381] pb-3">{{ $t('footer.help') }}</p>
          <ul class="space-y-3">
            <li><RouterLink to="/help" class="hover:opacity-80">{{ $t('footer.faq') }}</RouterLink></li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
</template>
